import {
  Box,
  VStack,
  Text,
  HStack,
  Flex,
  Skeleton,
  Center,
  Spinner,
  Badge,
  BoxProps,
  TextProps,
} from '@chakra-ui/react';
import { CalendarIcon, FileIcon, VisibilityIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { PageContext } from 'components/common/PageContext';
import { DateCell } from 'components/common/Table/Cells/DateCell';
import { TextCellWithIcon } from 'components/common/Table/Cells/TextCellWithIcon';
import { Table } from 'components/common/Table/Table';
import { TextCellProps } from 'components/common/Table/types/Cell';
import {
  useInfiniteWorksheetUserAnswers,
  Worksheet,
  WorksheetAnswerStatus,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { createColumns } from 'utils/tableHelper';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { Breakpoints } from 'types/breakpoints';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { WorksheetMenu } from 'components/worksheet/WorksheetMenu';

export type WorksheetData = Worksheet & {
  actionItem: ReactElement;
  answeredAt?: string | Date;
  status?: WorksheetAnswerStatus;
};

export type WorksheetAnswerData = WorksheetData & {
  answerId: string;
  worksheetId: string;
};
const statusMap: Record<
  WorksheetAnswerStatus,
  {
    label: string;
    color: BoxProps['bg'];
    textColor: TextProps['color'];
  }
> = {
  [WorksheetAnswerStatus.SUBMITTED]: {
    label: 'Abgegeben',
    color: 'tag.background.green',
    textColor: 'tag.text.green',
  },
  [WorksheetAnswerStatus.IN_PROGRESS]: {
    label: 'In Bearbeitung',
    color: 'tag.background.yellow',
    textColor: 'tag.text.yellow',
  },
  [WorksheetAnswerStatus.NOT_STARTED]: {
    label: 'Nicht begonnen',
    color: 'text.default',
    textColor: 'text.tertiary',
  },
};

const worksheetColumns = createColumns<WorksheetAnswerData>()({
  title: {
    header: 'Titel',
    element: (props: TextCellProps) => (
      <TextCellWithIcon
        label={props.label}
        iconBg="#F0EDFE"
        icon={<NovaIcon icon={FileIcon} size="s" color="#4D3C9E" aria-hidden />}
      />
    ),
    sortDisabled: true,
  },
  status: {
    header: 'Status',
    element: ({ label }: TextCellProps) => {
      const status = label as WorksheetAnswerStatus;
      const badge =
        status && status in statusMap ? statusMap[status as WorksheetAnswerStatus] : null;
      return badge ? (
        <Badge
          bg={badge.color}
          color={badge.textColor}
          borderRadius="m"
          px="2px"
          py="0.5px"
          fontSize="12px"
          fontWeight="medium"
        >
          {badge.label}
        </Badge>
      ) : null;
    },
    getCellProps: (row: WorksheetAnswerData) => ({
      label: row.status || '',
    }),
    sortDisabled: true,
  },
  answeredAt: { header: 'Beantwortet am', element: DateCell, sortDisabled: true },
});

const WorksheetCard = ({
  ws,
  onClick,
}: {
  ws: WorksheetAnswerData;
  onClick: (status: WorksheetAnswerStatus, answerId: string, worksheetId: string) => void;
}): ReactElement => (
  <Flex
    direction="row"
    align="center"
    bg="background.secondary"
    borderRadius="10px"
    px="m"
    py="14px"
    mb="12px"
    width="100%"
    minH="64px"
    onClick={() =>
      onClick(ws.status || WorksheetAnswerStatus.IN_PROGRESS, ws.answerId, ws.worksheetId)
    }
    cursor="pointer"
    _active={{ bg: 'background.secondary' }}
    _hover={{ bg: 'background.secondary' }}
    data-testid="worksheet-card"
  >
    <Center
      bg="background.secondary"
      borderRadius="s"
      p="2xs"
      h="28px"
      w="28px"
      minW="28px"
      overflow="hidden"
      flexShrink={0}
      bgColor="#F0EDFE"
      mr="10px"
      alignSelf="flex-start"
    >
      <FileIcon color="#4D3C9E" boxSize="19px" />
    </Center>
    <Box flex="1">
      <Flex gap="xs" align="start" mb="2px" flexWrap="wrap">
        <Text textStyle="body.m.medium" color="text.primary" lineClamp={2} flex="1" minW="0">
          {ws.title}
        </Text>
        {ws.status && (
          <Badge
            bg={statusMap[ws.status]?.color || statusMap[WorksheetAnswerStatus.IN_PROGRESS].color}
            color={
              statusMap[ws.status]?.textColor ||
              statusMap[WorksheetAnswerStatus.IN_PROGRESS].textColor
            }
            borderRadius="m"
            px="2px"
            py="0.5px"
            fontSize="12px"
            fontWeight="medium"
            flexShrink={0}
          >
            {statusMap[ws.status]?.label || statusMap[WorksheetAnswerStatus.IN_PROGRESS].label}
          </Badge>
        )}
      </Flex>
      <HStack gap="0">
        <NovaIcon icon={CalendarIcon} size="s" color="text.secondary" mr="4px" aria-hidden />
        <Text textStyle="body.xs.regular" color="text.tertiary">
          {ws.answeredAt ? new Date(ws.answeredAt as string).toLocaleDateString('de-DE') : ''}
        </Text>
      </HStack>
    </Box>
  </Flex>
);

const EmptyWorksheetUserAnswersState = (): ReactElement => (
  <Box
    width="100%"
    minHeight="60vh"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px="30px"
    my="auto"
  >
    <VStack gap="xs" textAlign="center">
      <Text textStyle="h4.medium" color="text.secondary">
        Noch keine Antworten vorhanden
      </Text>
      <Text textStyle="body.m.regular" color="text.tertiary">
        Sobald du ein Arbeitsblatt beantwortet hast, findest du deine Antworten hier.
      </Text>
    </VStack>
  </Box>
);

export const WorksheetUserAnswersDashboard = (): ReactElement => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteWorksheetUserAnswers(10);
  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);

  useEffect(() => {
    addBreadcrumbs({
      path: ProtectedRoutes.WORKSHEET_USER_ANSWERS_DASHBOARD,
      name: 'Arbeitsblätter',
    });
    return () => {
      removeBreadcrumbs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWsClick = useCallback(
    (wsId: string, extra?: Record<string, unknown>) => {
      const answerId = extra?.answerId as string | undefined;
      const status = extra?.status as WorksheetAnswerStatus | undefined;

      // If submitted but no answerId, do nothing
      if (status === WorksheetAnswerStatus.SUBMITTED && !answerId) {
        return;
      }

      const route =
        status === WorksheetAnswerStatus.SUBMITTED
          ? `${ProtectedRoutes.WORKSHEET_USER_ANSWERS_REVIEW}/${answerId}`
          : `${ProtectedRoutes.WORKSHEET_SESSION}/${wsId}`;

      navigate(route);
    },
    [navigate]
  );

  const mappedWorksheets = useMemo<WorksheetAnswerData[]>(() => {
    if (!data?.pages) return [];
    // Each page is WorksheetUserAnswersPage, so flatMap page.data
    return data.pages
      .flatMap((page) => page.data)
      .map((answer) => ({
        id: answer.worksheetId,
        answerId: answer.id,
        worksheetId: answer.worksheetId,
        content: [], // Not available in user list view
        description: '', // Not needed for user answers dashboard
        answeredAt: answer.answeredAt,
        title: answer.title,
        createdAt: answer.answeredAt, // Use answeredAt as fallback
        updatedAt: answer.updatedAt,
        status: answer.status,
        isPubliclyShared: false,
        isDistributed: true,
        actionItem: (
          <WorksheetMenu
            items={[
              {
                icon: <NovaIcon icon={VisibilityIcon} size="s" aria-hidden />,
                label: 'Ansehen',
                onClick: (e) => {
                  e.stopPropagation();
                  handleWsClick(answer.worksheetId, {
                    answerId: answer.id,
                    status: answer.status,
                  });
                },
              },
            ]}
          />
        ),
      }));
  }, [data, handleWsClick]);

  // Infinite scroll loader ref (only for desktop)
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const loader = loaderRef.current;

    if (!loader) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });
    observer.observe(loader);
    return () => {
      observer.disconnect();
    };
  }, [isDesktop, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <PageContext title="Arbeitsblätter" hasBackButton headTitle="Arbeitsblätter">
      <Box
        height="100%"
        minHeight="100%"
        flexDirection="column"
        maxWidth="100%"
        flexGrow={1}
        marginTop={mappedWorksheets.length > 0 ? { base: '40px', md: '0' } : {}}
        borderTop={{ base: 'default', md: 'none' }}
        marginBottom={{ base: '40px', md: '0' }}
        borderBottom={mappedWorksheets.length > 0 ? { base: 'default', md: 'none' } : {}}
        borderColor="border.accent"
      >
        {mappedWorksheets.length === 0 && <EmptyWorksheetUserAnswersState />}
        {mappedWorksheets.length > 0 && (
          <VStack
            bg="background.primary"
            align="left"
            p={{ base: '0', md: '20px 40px' }}
            gap={{ base: '0', md: 'l' }}
            width={{ base: '100vw', md: '100%' }}
            borderRadius={{ md: '2xl' }}
            minHeight={{ base: '100vh', md: 'unset' }}
          >
            {isDesktop && (
              <HStack gap="5px">
                <Box
                  bg="background.secondary"
                  borderRadius="s"
                  p="2xs"
                  display="flex"
                  alignItems="flex-start"
                  boxSize="26px"
                  mr="2px"
                >
                  <NovaIcon icon={FileIcon} size="m" color="icon.primary" aria-hidden />
                </Box>
                <Text textStyle="h4.regular" color="text.secondary">
                  Arbeitsblätter
                </Text>
              </HStack>
            )}
            {!isDesktop && (
              <Box width="100%" px="l" pt="0">
                {isLoading && (
                  <VStack gap="s" width="100%">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height="64px" borderRadius="10px" width="100%" />
                    ))}
                  </VStack>
                )}
                {!isLoading && mappedWorksheets && mappedWorksheets.length > 0 && (
                  <VStack gap="0" width="100%">
                    {mappedWorksheets.length > 0 &&
                      mappedWorksheets.map((ws) => (
                        <WorksheetCard
                          ws={ws}
                          key={ws.id}
                          onClick={() =>
                            handleWsClick(ws.id, {
                              answerId: ws.answerId,
                              status: ws.status ?? WorksheetAnswerStatus.IN_PROGRESS,
                            })
                          }
                        />
                      ))}
                  </VStack>
                )}
                {hasNextPage && (
                  <Box ref={loaderRef} width="100%" py="4px" textAlign="center">
                    {isFetchingNextPage && <Spinner size="sm" />}
                  </Box>
                )}
              </Box>
            )}
            {isDesktop && (
              <Box width="100%" px={{ base: 'l', md: '0' }}>
                <Table
                  columns={worksheetColumns}
                  data={mappedWorksheets || []}
                  isDataLoading={isLoading}
                  onRowClick={handleWsClick}
                  tableRowProps={{
                    cursor: 'pointer',
                  }}
                  tableHeadProps={{
                    textStyle: 'body.xs.medium',
                    color: 'text.tertiary',
                  }}
                />
                {hasNextPage && (
                  <Box ref={loaderRef} width="100%" py="4px" textAlign="center">
                    {isFetchingNextPage && <Spinner size="sm" />}
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </PageContext>
  );
};
